import { Button, cn, KBD, TypographyDetail } from '@seen/ui'
import { IconSearch } from '@tabler/icons-react'
import { type JSX, useCallback } from 'react'
import { useHotkeys as useModifierHotkeys } from 'react-hotkeys-hook'
import { Tooltip } from '#src/components/core/tooltip/Tooltip.tsx'
import { TooltipContent } from '#src/components/core/tooltip/TooltipContent.tsx'
import { TooltipTrigger } from '#src/components/core/tooltip/TooltipTrigger.tsx'
import { useDeskViewStore } from '#src/hooks/useDeskViewStore.ts'
import { useHotkeysStore } from '#src/hooks/useHotkeys.ts'
import { useSearchFocusRequestStore } from '#src/hooks/useSearchFocusRequestStore.ts'

/* The combo the desk's search field claims, which is what makes this the same
 * key rather than a second one that happens to agree: the request published
 * below is addressed to whichever `ShortcutSearchInput` binds this string, and
 * the list header's field passes it verbatim. `mod+shift+f` rather than `mod+f`
 * because a desk is not a document, so the browser's own find stays unbound.
 *
 * Unbound elsewhere: nothing else in the app binds it, Chrome leaves ⌘⇧F (and
 * Ctrl+Shift+F) free on every platform, and no tiptap keymap here claims it —
 * so a note being typed on the canvas is not competing for the keystroke. */
const DESK_SEARCH_SHORTCUT = 'mod+shift+f'
const DESK_SEARCH_SHORTCUT_LABEL = '⌘⇧F'

/* Canvas only, and both halves of one action: open the desk's search.
 *
 * The canvas has no search field of its own — the desk's notes are searchable
 * in list view — so this switches the desk over and hands the caret to the
 * field that mounts there. Two entry points, one path: the magnifier beside the
 * view switcher, and ⌘⇧F, which in list view is already the field's own combo.
 *
 * The button and the hotkey are the same component so the key is bound exactly
 * when the button is on screen. In list view the field binds the combo itself,
 * and this doesn't render — so the two never contend for one keystroke.
 *
 * Styled off the canvas's other floating controls (add-note, options) rather
 * than as a third segment inside the switcher: this doesn't select a view, so
 * it doesn't belong in a group whose members carry `aria-pressed`. `size="icon"`
 * resolves to `size-10`, the 40px the switcher beside it is drawn at, so the
 * band keeps one centered height. */
export function DeskSearchButton({
  deskId,
  className,
}: {
  deskId: string
  className?: string
}): JSX.Element {
  const setDeskView = useDeskViewStore((state) => state.setDeskView)
  const requestSearchFocus = useSearchFocusRequestStore(
    (state) => state.requestSearchFocus
  )

  /* The view switch and the focus request are published together, before the
   * list view exists. The request is what survives the gap: the field is behind
   * a lazy chunk and a Liveblocks room, so there is nothing to focus on this
   * tick, and the mount that eventually arrives may be rebuilt before it
   * settles. See useSearchFocusRequestStore. */
  const openSearch = useCallback(() => {
    setDeskView(deskId, 'list')
    requestSearchFocus(DESK_SEARCH_SHORTCUT)
  }, [deskId, setDeskView, requestSearchFocus])

  // Registered through react-hotkeys-hook directly for the reason
  // ShortcutSearchInput does it: the shared useHotkeys wrapper suppresses
  // single-letter hotkeys under a modifier, which would swallow this combo.
  // `enableOnContentEditable` because the canvas's own notes are contenteditable
  // and are usually where the caret is when this is pressed.
  const hotkeysDisabled = useHotkeysStore((state) => state.disabled)
  useModifierHotkeys(DESK_SEARCH_SHORTCUT, openSearch, {
    enabled: !hotkeysDisabled,
    preventDefault: true,
    enableOnFormTags: ['input', 'select', 'textarea'],
    enableOnContentEditable: true,
  })

  return (
    <Tooltip delayDuration={500}>
      <TooltipTrigger asChild>
        <Button
          variant="outline"
          size="icon"
          aria-label="Search notes"
          className={cn('shrink-0 bg-background hover:bg-accent', className)}
          onClick={openSearch}
        >
          <IconSearch className="size-4" />
        </Button>
      </TooltipTrigger>
      {/* Below, not beside, matching the switcher's own tooltips so the two
       * controls on this row behave the same. */}
      <TooltipContent className="flex items-center gap-2" side="bottom">
        <TypographyDetail>Search notes</TypographyDetail>
        <KBD>{DESK_SEARCH_SHORTCUT_LABEL}</KBD>
      </TooltipContent>
    </Tooltip>
  )
}
